import { useCallback, useMemo, useState } from 'react';
import { Blank, FaultLine, figure, unread, useRead } from '../../components/states/index.ts';
import { Select } from '../../components/Field.tsx';
import { Panel } from '../../components/Panel.tsx';
import { Icon } from '../../shell/Icon.tsx';
import {
  duration, latlon, midEllipsis, NO_VALUE, num, relative, utc,
} from '../../components/format.ts';
import { LIVENESS_STATE, STATUS_STATE } from '../../components/state.ts';
import { axisClock } from '../../components/timeScale.ts';
import { useNow } from '../../components/useNow.ts';
import { useRobotCommands } from '../../hooks/useCommands.ts';
import { useRobots, type Robot, type RobotStatus } from '../../hooks/useRobots.ts';
import { RobotLink } from '../../components/AppLink.tsx';
import { useTelemetry, type TelemetryReading } from '../../hooks/useTelemetry.ts';
import {
  ChannelRail, CHANNELS, commandMarks, CursorCtx, dangerHigh, DEFAULT_CHANNELS, extremeIn,
  markAt, presenceRuns, quoteAt, readingNum, readingStr, ReadingsLedger, samplesIn,
  seriesColour, SignalStack, StateStripCard, stampOf, TimeSeriesCard, TrackCard,
  TransportBar, useTransport, worstIn,
  type ChannelRow, type ChartSeries, type RailFact, type RailGroup,
  type RobotRow, type TimeAxis,
} from '../../components/charts/index.ts';
import { useCompareTelemetry } from '../../components/charts/useCompare.ts';
import styles from './TelemetryWorkspace.module.css';

/* ==========================================================================
   Signal explorer — /telemetry.

   Four regions, no page scroll, one window and one cursor across all of them:

     RAIL        the channel index. Every channel carries its own verdict dot
                 (`auterion-flight-log-review.png`), so the operator sees
                 WHICH subsystem is bad before opening anything, plus the
                 robot roster, the reading under the cursor and the window's
                 provenance.
     INSTRUMENT  the plots. See THE TWO VIEWS below.
     LEDGER      the same readings as rows, bound to the same window and the
                 same cursor: the trace says when, the row says what else was
                 true at that instant.
     TRANSPORT   LIVE/REPLAY, the data-presence track, the span, the axis and
                 the Zulu clock. Pinned to the foot, because every panel above
                 it is bound to it and it can never be the thing that scrolls
                 away.

   THE TWO VIEWS, AND WHY THE ROSTER PICKS
     One robot asks "is anything wrong with this machine", and the answer is
     a vertical read across every channel at one instant — so the channels
     are LANES on ONE x-axis, with the mode ribbon as the top lane of the same
     SVG. Side-by-side cards cannot answer it: correlating a CPU spike with a
     speed change across a panel seam is the operator's eye doing arithmetic.

     Two robots ask "is it this machine or all of them", and that is a
     different shape: one CARD per channel, one named LANE per robot inside
     it, never overplotted (`formant-fleet-observability-dashboard.png`).

     So the view is a consequence of the roster rather than a control the
     operator has to find: adding a comparand switches it, removing the last
     one switches back. Nothing else about the surface moves — same window,
     same cursor, same rail, same ledger.

   WHAT THE SURFACE REFUSES TO DO
     · interpolate across a coverage hole. A gap in the record is a gap in
       the trace and a gap in the presence band.
     · quote a value for an instant the robot was not reporting at. Past one
       cadence from the cursor every readout says `—`.
     · hide a limit. Every threshold a channel is judged on is a rule on the
       plot, grey until this window crosses it.
     · page by row count. `100 | 300 | 600` is eight hours on a 2m24s robot
       and half an hour on a 10s one — the range is time, and the ledger's
       depth is a consequence of it.
   ========================================================================== */

/** The store holds a day per robot; ask for all of it and window client-side. */
const DEPTH = 600;

type LedgerSize = 'off' | 'mid' | 'tall';
const SIZES: readonly LedgerSize[] = ['off', 'mid', 'tall'];

export function TelemetryWorkspace({
  robotId,
  robots,
  initialCompare,
}: {
  robotId: string | null;
  robots: readonly Robot[];
  /** Robots the comparison opens with. Used by the kit's specimen page. */
  initialCompare?: readonly string[] | undefined;
}) {
  const now = useNow();
  const transport = useTransport(now, '6h');

  const [compare, setCompare] = useState<readonly string[]>(initialCompare ?? []);
  const [hiddenRobots, setHiddenRobots] = useState<ReadonlySet<string>>(new Set());
  const [shownChannels, setShownChannels] = useState<ReadonlySet<string>>(
    () => new Set(DEFAULT_CHANNELS),
  );
  const [axis, setAxis] = useState<TimeAxis>('agent');
  /* HOW MUCH OF THE CANVAS THE LEDGER TAKES.

     Three stops, not a toggle. The instrument and the record are two views of
     one window and which of them is the subject changes minute to minute —
     reading a trace wants the chart at full height, checking a number wants
     twenty rows. A drag handle would be a better control and a worse
     screenshot; three stops is the whole range in two always-visible
     buttons. */
  const [ledger, setLedger] = useState<LedgerSize>('mid');
  const ledgerOpen = ledger !== 'off';
  /* One card at a time may take the comparison grid. The window and the
     cursor are untouched by it — expanding is a change of magnification, not
     of state, so the ground track and the ledger go on tracking the same
     instant. */
  const [expanded, setExpanded] = useState<string | null>(null);

  const primaryId = robotId ?? undefined;

  /* The subject keeps the shared `['telemetry', …]` key so the shell can
     resolve the breadcrumb's robot and the status bar's reading count from
     it; the comparands are fetched under their own key. See useCompare.ts. */
  const primaryQuery = useTelemetry(primaryId, { limit: DEPTH });
  const { data: primaryRows, isLoading: primaryLoading } = primaryQuery;
  /* THE INSTRUMENT SURVIVES THE READ. This surface used to answer a failed
     telemetry read, a first read still in flight and an empty roster by
     REPLACING its whole canvas — nine lanes, the channel rail, the axis, the
     ground track and the ledger — with a bare `Panel` holding one sentence.
     All three are the same event as far as the drawing is concerned: the
     chrome is true and only the readings are absent, so the stack is drawn in
     every one of them and the fact goes on the one notice row inside it
     (§11.6 state 1). `useRead` is what tells the three apart. */
  const telemetryRead = useRead(primaryQuery, {
    path: primaryId === undefined ? undefined : `/api/robots/${primaryId}/telemetry`,
    everyMs: 5_000,
    subject: primaryId ?? 'this robot',
  });
  const rosterRead = useRead(useRobots(), {
    path: '/api/robots',
    everyMs: 5_000,
    subject: 'the fleet register',
    empty: robots.length === 0,
  });
  const compareRows = useCompareTelemetry(compare, DEPTH);

  /* WHAT WE DID TO THE ROBOT, ON THE AXIS OF WHAT THE ROBOT DID.

     Commands are this console's events, and §5 wants them as marks at their
     true timestamps rather than as a list on another route. Only the
     SUBJECT's: a comparison is asking whether a symptom is one machine's or
     the fleet's, and three robots' dispatch histories on one axis would be
     three questions at once. */
  const { data: commandRows } = useRobotCommands(primaryId);
  const events = useMemo(
    () => commandMarks(commandRows ?? [], now),
    [commandRows, now],
  );
  /* Inside the drawn window only — a mark the operator cannot see is a mark
     that should not be counted in the strip's caption either. */
  const windowEvents = useMemo(
    () => events.filter((e) => (e.end ?? e.at) >= transport.from && e.at <= transport.to),
    [events, transport.from, transport.to],
  );

  const byId = useMemo(() => new Map(robots.map((r) => [r.robot_id, r])), [robots]);

  /* Colour is keyed on the comparison's full membership, not on how many
     lanes happen to be visible: hiding a robot must not repaint the others. */
  const roster = useMemo(
    () => (primaryId === undefined ? [] : [primaryId, ...compare]),
    [primaryId, compare],
  );

  const allSeries: ChartSeries[] = useMemo(
    () =>
      roster.map((id, i) => ({
        id,
        colour: seriesColour(i, roster.length),
        readings:
          id === primaryId
            ? (primaryRows ?? [])
            : (compareRows.find((c) => c.robotId === id)?.readings ?? []),
      })),
    [roster, primaryId, primaryRows, compareRows],
  );

  const series = useMemo(
    () => allSeries.filter((s) => !hiddenRobots.has(s.id)),
    [allSeries, hiddenRobots],
  );

  /* The roster picks the view — see the header note. */
  const comparing = series.length > 1;

  /* THE RAIL IS THE SUBJECT'S INDEX, AND ONLY THE SUBJECT'S.

     Its dot used to latch over every visible robot's readings while its
     number quoted the subject's, so adding a faulted comparand turned the
     subject's Battery row red while it went on printing 79.9 % — one row
     making two claims about two machines. The comparands are not
     unrepresented by this: each has its own lane in every card, recoloured
     where it breaches, and its own row in ROBOTS. */
  const railSamples = useMemo(
    () =>
      CHANNELS.map((c) =>
        samplesIn(primaryRows ?? [], c, transport.from, transport.to, axis),
      ),
    [primaryRows, transport.from, transport.to, axis],
  );

  const channelRows: ChannelRow[] = useMemo(
    () =>
      CHANNELS.map((c, i) => {
        const s = railSamples[i]!;
        /* WHICH SAMPLE THE RAIL QUOTES — `quoteAt`'s rule, and the reason it
           exists. With a cursor on the plot the question is about an INSTANT
           and the answer is that instant or `—`. With no cursor the question
           is "what is it now", and the answer is the newest reading with its
           age said once (the AT CURSOR eyebrow carries it) — because a robot
           four cadences stale is the ordinary case here and nine rows of `—`
           is the surface refusing to say what it plainly knows. */
        const { i: idx, stale } = quoteAt(s, transport.at, transport.to);
        const v = idx >= 0 ? s.v[idx] : null;
        /* THE EXCURSION, PRINTED. The dot latches the window's worst level;
           this is the reading that earned it, at the end of the scale the
           channel is actually in danger at (low for battery and free disk,
           high for CPU and temperature). It is drawn only on a channel that
           breached, so a healthy rail carries no arrows at all and the third
           column IS the alarm. */
        const verdict = worstIn(s);
        const extreme = verdict === 'warn' || verdict === 'bad'
          ? extremeIn(c, s)
          : null;
        const quoted = v == null ? NO_VALUE : v.toFixed(c.digits);
        /* Not when the cursor is ALREADY standing on the worst of it. The
           excursion column exists to say "it got worse than what you are
           looking at"; on `koko-04`, whose battery is at its own minimum
           right now, it would print `↓7.9 7.9 %` — one number twice, which
           reads as a fault in the rail rather than in the robot. The value's
           own tone is already carrying the breach in that case. */
        const peak = extreme === null || extreme.toFixed(c.digits) === quoted
          ? null
          : extreme;
        return {
          key: c.key,
          label: c.label,
          unit: c.unit,
          verdict,
          ...(peak === null
            ? {}
            : { peak: `${dangerHigh(c) ? '↑' : '↓'}${peak.toFixed(c.digits)}` }),
          value: quoted,
          level: idx >= 0 ? s.lv[idx]! : 'unknown',
          stale,
          shown: shownChannels.has(c.key),
          hint: c.hint,
        };
      }),
    [railSamples, transport.at, transport.to, shownChannels],
  );

  /* The reading the cursor is standing on, for the fields no channel plots:
     the mode, the fix, the agent's own sequence number. Same tolerance rule
     as every other readout — one cadence, or `—`. */
  const atReading = useMemo<TelemetryReading | null>(() => {
    const rows = primaryRows ?? [];
    if (rows.length === 0) return null;
    const t: number[] = [];
    const keep: TelemetryReading[] = [];
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      const r = rows[i]!;
      const ts = stampOf(r, axis);
      if (!Number.isFinite(ts) || ts < transport.from || ts > transport.to) continue;
      t.push(ts);
      keep.push(r);
    }
    if (t.length === 0) return null;
    const deltas: number[] = [];
    for (let i = 1; i < t.length; i += 1) deltas.push(t[i]! - t[i - 1]!);
    deltas.sort((a, b) => a - b);
    const cadence = deltas.length > 0 ? deltas[deltas.length >> 1]! : 0;
    const { i: idx } = quoteAt(
      {
        t,
        v: t.map(() => 0),
        lv: [],
        cadence,
        gapMs: 0,
        lo: null,
        hi: null,
        count: t.length,
      },
      transport.at,
      transport.to,
    );
    return idx >= 0 ? (keep[idx] ?? null) : null;
  }, [primaryRows, axis, transport.from, transport.to, transport.at]);

  /* THE AGE OF THE RESTING QUOTE.

     With no cursor every readout on the surface is quoting the newest reading
     in the window, and §13 tell 24 is unambiguous about the obligation that
     creates: a "latest" has to say how late. It is said once — on the AT
     CURSOR eyebrow and in the instrument's caption — rather than as a suffix
     on nine values. */
  const edge = useMemo(() => {
    let newest: number | null = null;
    let cad = 0;
    const t: number[] = [];
    for (const r of primaryRows ?? []) {
      const ts = stampOf(r, axis);
      if (!Number.isFinite(ts) || ts < transport.from || ts > transport.to) continue;
      t.push(ts);
      if (newest === null || ts > newest) newest = ts;
    }
    t.sort((a, b) => a - b);
    const d: number[] = [];
    for (let i = 1; i < t.length; i += 1) d.push(t[i]! - t[i - 1]!);
    d.sort((a, b) => a - b);
    cad = d.length > 0 ? d[d.length >> 1]! : 0;
    const age = newest === null ? 0 : transport.to - newest;
    return { newest, age, stale: newest !== null && age > (cad > 0 ? cad : 60_000) };
  }, [primaryRows, axis, transport.from, transport.to]);

  /* Provenance for the window, about the SUBJECT's agent: how often it spoke,
     whether the sequence it numbered is whole, which boot produced these rows
     and how far its clock sits from the ingest tier's. */
  const windowFacts: RailFact[] = useMemo(() => {
    const rows = (primaryRows ?? []).filter((r) => {
      const ts = stampOf(r, axis);
      return ts >= transport.from && ts <= transport.to;
    });
    if (rows.length === 0) {
      /* A ZERO IS A MEASUREMENT. AN UNREAD SOURCE IS NOT — and this is the row
         it is most tempting on, because `Readings 0` is literally true of the
         array the block was handed. It is not true of the window: nobody has
         counted anything. The em dash and the fault line on the instrument
         above are the honest pair. */
      return [
        {
          key: 'readings', label: 'Readings',
          value: unread(telemetryRead) ? NO_VALUE : '0',
          title: unread(telemetryRead)
            ? `${telemetryRead.path ?? 'This source'} has not answered — this is not a zero`
            : 'Rows inside the window.',
        },
      ];
    }
    const t = rows.map((r) => stampOf(r, axis)).sort((a, b) => a - b);
    const deltas: number[] = [];
    for (let i = 1; i < t.length; i += 1) deltas.push(t[i]! - t[i - 1]!);
    deltas.sort((a, b) => a - b);
    const cadence = deltas.length > 0 ? deltas[deltas.length >> 1]! : 0;
    const runs = presenceRuns(t, cadence * 2.5, cadence);
    const gaps = Math.max(0, runs.length - 1);
    const span = Math.max(1, transport.to - transport.from);
    const coveredPct = runs.reduce(
      (acc, r) =>
        acc + (Math.min(r.end, transport.to) - Math.max(r.start, transport.from)),
      0,
    ) / span * 100;

    const seqs = rows
      .map((r) => readingNum(r, 'seq'))
      .filter((n): n is number => n !== null);
    const seqSpan =
      seqs.length > 1 ? Math.max(...seqs) - Math.min(...seqs) + 1 : seqs.length;
    const boots = [...new Set(rows.map((r) => readingStr(r, 'agent_boot_id')).filter(
      (b): b is string => b !== null,
    ))];
    const skews = rows
      .map((r) => readingNum(r, 'clock_offset_ms'))
      .filter((n): n is number => n !== null)
      .map(Math.abs);
    const worstSkew = skews.length > 0 ? Math.max(...skews) : null;

    return [
      {
        key: 'readings', label: 'Readings', value: num(rows.length),
        title: 'Rows the agent delivered inside this window.',
      },
      {
        key: 'cadence', label: 'Cadence', value: duration(cadence),
        title: 'Median interval between consecutive readings.',
      },
      /* Coverage and holes are one question asked two ways — how much of
         this window is real — so they share a row. `76% · 0 holes` is the
         common and confusing case: a robot that stopped reporting has no
         BREAK in its coverage, it has a short band, and the two numbers only
         make sense read together. */
      {
        key: 'covered', label: 'Covered',
        value: `${Math.min(100, coveredPct).toFixed(0)}% · ${gaps} ${gaps === 1 ? 'hole' : 'holes'}`,
        tone: coveredPct < 90 || gaps > 0 ? 'warn' : 'neutral',
        title:
          'Share of the window the agent actually reported over, at its own '
          + 'cadence, and the number of breaks INSIDE that coverage (longer '
          + 'than 2.5 cadences with nothing in them). Under 100% means part of '
          + 'this window is not "no change", it is nothing received; a robot '
          + 'that simply stopped has zero holes and a short band.',
      },
      {
        key: 'seq', label: 'Delivery',
        value: seqs.length === 0 ? NO_VALUE : `${num(seqs.length)}/${num(seqSpan)}`,
        tone: seqs.length > 0 && seqs.length < seqSpan ? 'warn' : 'neutral',
        title:
          'Readings received against the span of sequence numbers the agent '
          + 'issued. Short means frames were dropped between the robot and here.',
      },
      {
        key: 'boot', label: 'Boot',
        value: boots.length === 0
          ? NO_VALUE
          : boots.length === 1
            ? midEllipsis(boots[0]!, 11)
            : `${boots.length} boots`,
        tone: boots.length > 1 ? 'warn' : 'neutral',
        title: boots.length > 1
          ? `The agent restarted inside this window: ${boots.join(', ')}`
          : `agent_boot_id ${boots[0] ?? NO_VALUE}`,
      },
      {
        key: 'skew', label: 'Clock skew',
        value: worstSkew === null ? NO_VALUE : `${worstSkew.toFixed(1)} ms`,
        title: "Largest gap between the agent's wall clock and the ingest tier's, in this window.",
      },
    ];
  }, [primaryRows, axis, transport.from, transport.to, telemetryRead]);

  const subject = primaryId === undefined ? undefined : byId.get(primaryId);

  /* The command that owns the instant under the cursor. */
  const cmd = useMemo(
    () => markAt(events, transport.at ?? transport.to),
    [events, transport.at, transport.to],
  );

  /* §13 tell 23 — provenance that stops at the robot name is not provenance.
     Every one of these is a field the API actually returns for this robot. */
  const cursorFacts: RailFact[] = useMemo(() => {
    const r = atReading;
    const mode = r?.state ?? null;
    const desc = mode !== null && mode in STATUS_STATE
      ? STATUS_STATE[mode as RobotStatus]
      : null;
    const seq = r === null ? null : readingNum(r, 'seq');
    return [
      {
        key: 'mode', label: 'Mode',
        value: desc === null ? NO_VALUE : desc.label,
        tone: desc?.tone ?? 'neutral',
        title: desc?.hint ?? 'The agent’s own `state` field at this instant.',
      },
      {
        key: 'fix', label: 'Fix',
        value: r === null ? NO_VALUE : latlon(r.lat, r.lon),
        title: 'WGS84 latitude and longitude the agent reported with this reading.',
        wide: true,
      },
      {
        key: 'seq', label: 'Seq',
        value: seq === null ? NO_VALUE : String(seq),
        title: 'The sequence number the agent stamped this reading with, within its boot.',
      },
      /* NO `Offset` ROW HERE. The reading's own clock offset is already a
         CHANNEL — `Clock skew` — and the rail's channel list two blocks above
         quotes it at this same cursor. A rail that prints one number twice
         under two names is a rail the operator has to reconcile.

         WHAT WE HAD ASKED IT TO DO AT THIS INSTANT.

         The command still running at the cursor, or failing that the newest
         one issued before it — the readout that turns the diamonds on the
         plot into an answer. Null before the first dispatch: a window that
         opens before anyone touched the robot has no last command, and
         naming one would be an invention. */
      {
        key: 'cmd', label: 'Command',
        value: cmd === null ? NO_VALUE : cmd.label,
        /* The state word is printed, not only coloured. */
        ...(cmd === null ? {} : { suffix: cmd.status }),
        tone: cmd === null ? 'neutral' : cmd.tone,
        title: cmd === null
          ? 'No command had been issued to this robot at this instant.'
          : cmd.detail,
        wide: true,
      },
    ];
  }, [atReading, cmd]);

  const sourceFacts: RailFact[] = useMemo(() => {
    if (subject === undefined) return [];
    const meta = subject.meta as Record<string, unknown> | null | undefined;
    const kind = typeof meta?.['kind'] === 'string' ? (meta['kind'] as string) : null;
    const site = typeof meta?.['site_label'] === 'string'
      ? (meta['site_label'] as string)
      : (typeof meta?.['site'] === 'string' ? (meta['site'] as string) : null);
    /* ORDERED BY URGENCY, NOT BY TAXONOMY. The rail scrolls on a short
       viewport, so the fact most likely to be the reason the operator opened
       this robot goes first: how long ago it last said anything. */
    return [
      {
        key: 'seen', label: 'Last seen',
        value: relative(subject.last_seen, now),
        title: subject.last_seen === null
          ? 'This robot has never reported.'
          : `Newest reading the platform holds: ${utc(subject.last_seen)}`,
      },
      {
        key: 'plant', label: 'Plant',
        value: kind === null ? NO_VALUE : kind === 'sim' ? 'Simulated' : 'Hardware',
        tone: kind === 'sim' ? 'warn' : 'neutral',
        title: kind === 'sim'
          ? 'meta.kind = sim — these readings come from Isaac Sim, not from a machine.'
          : 'meta.kind — whether the readings come from real hardware or from Isaac Sim.',
      },
      {
        key: 'build', label: 'Agent',
        value: subject.firmware ?? NO_VALUE,
        title: 'The platform build the agent reported at enrolment.',
      },
      {
        key: 'site', label: 'Site', value: site ?? NO_VALUE,
        title: 'Where the robot is deployed.',
        wide: true,
      },
    ];
  }, [subject, now]);

  const groups: RailGroup[] = useMemo(
    () => [
      {
        key: 'cursor',
        label: 'At cursor',
        /* WHICH INSTANT THIS BLOCK IS READING, WITH THE INSTANT IN IT.
           The block is 900px from the stack's caption and quotes four fields
           no lane plots, so `hover` alone leaves the operator to date it off
           a panel at the other end of the screen. Same word-then-instant
           stamp the instruments carry, accent while a crosshair rather than
           the newest sample is the source. */
        aside: transport.at !== null ? (
          <>
            {transport.hover !== null ? 'hover ' : 'pinned '}
            <span className={styles.cursorStamp}>{axisClock(transport.at)}</span>
          </>
        ) : edge.stale ? (
          /* RESTING, THE AGE IS THE STAMP. `newest · 24m 13s old` answers the
             question this eyebrow is asked at rest — how late is this — in a
             form an absolute Zulu time does not, and the eyebrow is 130px
             wide: printing both clipped the clock away silently. The absolute
             instant is on the stack's caption and the ledger's, both bound to
             this same cursor. */
          `newest · ${duration(edge.age)} old`
        ) : (
          'newest'
        ),
        facts: cursorFacts,
      },
      {
        key: 'window',
        label: 'Window',
        aside: transport.live && transport.playhead === null ? 'live' : 'replay',
        facts: windowFacts,
      },
      {
        key: 'source',
        label: 'Source',
        /* THE WAY OFF THIS SURFACE. `/telemetry` had exactly one robot
           control — the crumb's picker — and nothing at all that opened the
           robot it is plotting, so an operator who found something here had
           to retype the id into the fleet list to act on it. */
        aside:
          primaryId === undefined ? (
            ''
          ) : (
            <RobotLink
              id={primaryId}
              title={`Open ${primaryId} — its command queue, its captures, its registry record`}
            />
          ),
        facts: sourceFacts,
      },
    ],
    [
      cursorFacts, windowFacts, sourceFacts, primaryId, edge,
      transport.at, transport.hover, transport.live, transport.playhead,
    ],
  );

  /* Per-robot skew: how far each lane's newest reading in the window is
     behind the window's right edge. §11.3 — one freshness answer per stream,
     never one per page — applied to a comparison, where the whole question is
     which machine is behind. */
  const robotAge = useMemo(() => {
    const out = new Map<string, string>();
    for (const s of allSeries) {
      const t: number[] = [];
      for (const r of s.readings) {
        const ts = stampOf(r, axis);
        if (Number.isFinite(ts) && ts >= transport.from && ts <= transport.to) t.push(ts);
      }
      if (t.length === 0) continue;
      t.sort((a, b) => a - b);
      const d: number[] = [];
      for (let i = 1; i < t.length; i += 1) d.push(t[i]! - t[i - 1]!);
      d.sort((a, b) => a - b);
      const cad = d.length > 0 ? d[d.length >> 1]! : 0;
      const gap = transport.to - t[t.length - 1]!;
      if (gap > (cad > 0 ? cad : 60_000)) out.set(s.id, duration(gap));
    }
    return out;
  }, [allSeries, axis, transport.from, transport.to]);

  const robotRows: RobotRow[] = useMemo(
    () =>
      allSeries.map((s) => {
        const r: Robot | undefined = byId.get(s.id);
        const live = r === undefined ? null : LIVENESS_STATE[r.liveness];
        return {
          id: s.id,
          colour: s.colour,
          age: robotAge.get(s.id),
          tone: live?.tone ?? 'neutral',
          hollow: live?.hollow ?? true,
          liveness: live?.label ?? NO_VALUE,
          shown: !hiddenRobots.has(s.id),
          /* The one visible series cannot be switched off — see toggleRobot. */
          locked: allSeries.length - hiddenRobots.size <= 1 && !hiddenRobots.has(s.id),
          primary: s.id === primaryId,
        };
      }),
    [allSeries, byId, hiddenRobots, primaryId, robotAge],
  );

  /* CLICKING A CHANNEL IS A MAGNIFICATION, NOT A CHECKBOX.

     A plain click ISOLATES the channel — one lane taking the whole
     instrument, with its limits, its ribbon and the crosshair. Clicking the
     isolated one again restores the default set. ⇧/⌘-click adds a second
     channel to the same x-axis, which is how two signals get compared: the
     stack shares one window and one cursor, so two lanes are already
     aligned to the instant. The last visible channel cannot be removed —
     an instrument with nothing on it is not a state worth being able to
     reach by accident. */
  const clickChannel = useCallback((key: string, additive: boolean) => {
    setShownChannels((prev) => {
      if (additive) {
        const next = new Set(prev);
        if (next.has(key)) {
          if (next.size > 1) next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      }
      if (prev.size === 1 && prev.has(key)) return new Set(DEFAULT_CHANNELS);
      return new Set([key]);
    });
    setExpanded(null);
  }, []);

  /* The last visible robot cannot be hidden. An instrument with no series on
     it is not a state worth being able to reach by accident, and the rail
     would go on quoting the subject's numbers beside plots that were no
     longer drawing them. */
  const toggleRobot = useCallback((id: string) => {
    setHiddenRobots((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const removeRobot = useCallback((id: string) => {
    setCompare((prev) => prev.filter((x) => x !== id));
    setHiddenRobots((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  /* The comparison picker, grouped by the two things that make a comparand
     worth adding: it is faulted, or it is the same kind of machine. A flat
     28-row list of ids is a list you have to already know the answer to use. */
  const addable = useMemo(() => robots.filter((r) => !roster.includes(r.robot_id)), [robots, roster]);
  const addGroups = useMemo(() => {
    const faulted = addable.filter((r) => r.status === 'error');
    const kin = addable.filter(
      (r) => r.status !== 'error' && r.morphology === subject?.morphology,
    );
    const rest = addable.filter(
      (r) => r.status !== 'error' && r.morphology !== subject?.morphology,
    );
    return [
      { label: 'Faulted', rows: faulted },
      { label: subject === undefined ? 'Same morphology' : `${subject.morphology}`, rows: kin },
      { label: 'Rest of fleet', rows: rest },
    ].filter((g) => g.rows.length > 0);
  }, [addable, subject]);

  const onExpand = useCallback((key: string, next: boolean) => {
    setExpanded(next ? key : null);
  }, []);

  const visibleChannels = useMemo(
    () => CHANNELS.filter((c) => shownChannels.has(c.key)),
    [shownChannels],
  );

  /* THE LEDGER'S COLUMNS DO NOT FOLLOW THE INSTRUMENT'S SELECTION.

     Isolating Battery is a question about the picture, not about the record —
     and a table whose columns appear and disappear as a chart above it is
     focused is a table you cannot keep your place in. It is also what keeps
     the widths honest: thirteen columns leave the panel with a couple of
     hundred pixels to hand out, where five would leave six hundred and every
     numeric column would drift to twice the width of its own longest value.
     `Clock skew` is not among them because the ledger prints that field as
     its own `Offset` column. */
  const ledgerChannels = useMemo(() => CHANNELS.filter((c) => !c.secondary), []);

  /* A card must be tall enough for every robot's lane: an identity strip and
     a plot each, or the third comparand is clipped clean off the bottom of a
     panel that gives no sign it is holding one. */
  /* 44, not 42: the panel's header and ruler are 28 and 14, and the two
     pixels of border under them are what left a six-robot card two pixels
     short of its own last lane. */
  const cardMin = 44 + Math.max(1, series.length) * 39;
  /* Expanding hides the other cards rather than restacking them: a grid that
     scrolls a nine-card layout under one enlarged panel is two layouts at
     once, and the operator loses the card they were reading. */
  const gridChannels = expanded === null
    ? visibleChannels
    : CHANNELS.filter((c) => c.key === expanded);
  const hasAnyReading = allSeries.some((s) => s.readings.length > 0);

  /* WHEN THE WINDOW IS OVER A HOLE.

     An offline robot's newest reading can be seven hours behind the window,
     and the instrument then draws nine lanes each saying "nothing arrived in
     this window". So the surface answers once, names where the coverage
     actually is, and offers the window that reaches it. The presence band
     underneath is already showing the same fact as a shape. */
  const coverage = useMemo(() => {
    let newest: number | null = null;
    let inWindow = 0;
    for (const s of series) {
      for (const r of s.readings) {
        const ts = stampOf(r, axis);
        if (!Number.isFinite(ts)) continue;
        if (newest === null || ts > newest) newest = ts;
        if (ts >= transport.from && ts <= transport.to) inWindow += 1;
      }
    }
    return { newest, inWindow };
  }, [series, axis, transport.from, transport.to]);

  const robotNames = useMemo(
    () => new Map(robots.map((r) => [r.robot_id, r.robot_id])),
    [robots],
  );

  /* THREE FAILURES REPLACE THE INSTRUMENT; TWO DO NOT.

     A read that failed, a first read that has not landed and a fleet with no
     robots in it are states where there is no instrument to draw — no
     channels, no axis, nothing the operator could point at. Those keep a
     panel and one line.

     A robot that has never reported, and a window that sits off the end of
     the coverage, are NOT those. The channels exist, the axis exists, the
     window exists — only the trace is missing — and replacing nine lanes
     with a centred sentence would change the shape of the surface because a
     machine went quiet, which §8 tell 3 forbids and which also throws away
     the thing the operator most needs to see: WHICH channels this robot
     would have reported. Those two draw the instrument empty and say so on
     one row inside it (§11.6 state 1: "vitals tracks drawn with no arc and
     `—`"). */
  /* A read that failed outranks an absence: `no telemetry has ever arrived`
     is a claim about the ROBOT, and it may only be made once the platform has
     answered. */
  const faulted =
    telemetryRead.fault !== null ? telemetryRead
      : robots.length === 0 && rosterRead.fault !== null ? rosterRead
        : null;

  const emptyKind =
    faulted !== null ? null
      : robots.length === 0 ? 'no-robots'
        : !hasAnyReading ? 'never'
          : coverage.inWindow === 0 && coverage.newest !== null ? 'out-of-window'
            : null;

  const notice: React.ReactNode =
    faulted !== null ? (
      <FaultLine read={faulted} />
    ) : emptyKind === 'no-robots' ? (
      <Blank noun="robots" where="enrolled" />
    ) : emptyKind === 'never' ? (
      <>
        <span>No telemetry has ever arrived for this robot</span>
        <span className={styles.noticeDim}>
          {subject === undefined
            ? `/api/robots/${primaryId ?? '…'}/telemetry`
            : `${subject.robot_id} · ${LIVENESS_STATE[subject.liveness].label}`
              + (subject.last_seen === null
                ? ' · never reported'
                : ` · last seen ${utc(subject.last_seen)}`)}
        </span>
      </>
    ) : emptyKind === 'out-of-window' ? (
      <>
        <span>{`Nothing in this window — ${primaryId ?? 'this robot'} stopped reporting before it`}</span>
        <span className={styles.noticeDim}>
          {`newest ${utc(new Date(coverage.newest!).toISOString())} · ${
            duration(transport.from - coverage.newest!)
          } before it opens`}
        </span>
        <span className={styles.noticeSpacer} />
        <button
          type="button"
          className={styles.noticeBtn}
          onClick={() => {
            const span = transport.to - transport.from;
            transport.zoomTo(coverage.newest! - span, coverage.newest!);
          }}
        >
          Move the window onto it
        </button>
      </>
    ) : undefined;

  const step = (delta: number) => {
    const i = SIZES.indexOf(ledger);
    const next = SIZES[Math.max(0, Math.min(SIZES.length - 1, i + delta))];
    if (next !== undefined) setLedger(next);
  };
  const ledgerTool = (
    <>
      <button
        type="button"
        className={styles.paneBtn}
        disabled={ledger === 'tall'}
        aria-label="Give the ledger more of the canvas"
        title="Give the ledger more of the canvas — the instrument keeps the window"
        onClick={() => step(1)}
      >
        <Icon name="chevron-up" size={13} />
      </button>
      <button
        type="button"
        className={styles.paneBtn}
        disabled={ledger === 'off'}
        aria-label={ledger === 'mid' ? 'Collapse the ledger' : 'Give the instrument more of the canvas'}
        title={
          ledger === 'mid'
            ? 'Collapse the ledger and give the whole canvas to the instrument'
            : 'Give the instrument more of the canvas'
        }
        onClick={() => step(-1)}
      >
        <Icon name="chevron-down" size={13} />
      </button>
    </>
  );

  return (
    <div className={styles.canvas} data-shell-canvas="true">
      <CursorCtx.Provider value={transport}>
        <div className={styles.layout}>
          <ChannelRail
            channels={channelRows}
            robots={robotRows}
            /* The roster is `/api/robots`', not telemetry's: `ROBOTS 0` is a
               census of the fleet register. */
            read={rosterRead}
            groups={groups}
            onToggleChannel={clickChannel}
            onToggleRobot={toggleRobot}
            onRemoveRobot={removeRobot}
            adder={
              <Select
                size="sm"
                aria-label="Add a robot to the comparison"
                value=""
                disabled={addGroups.length === 0}
                onChange={(e) => {
                  const id = e.target.value;
                  if (id) setCompare((prev) => [...prev, id]);
                }}
              >
                <option value="">
                  {addGroups.length === 0 ? 'No other robots' : 'Compare with…'}
                </option>
                {addGroups.map((g) => (
                  <optgroup key={g.label} label={`${g.label} (${g.rows.length})`}>
                    {g.rows.map((r) => (
                      <option key={r.robot_id} value={r.robot_id}>
                        {r.robot_id}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </Select>
            }
          />

          <div className={styles.content} data-ledger={ledger}>
              <>
                {comparing ? (
                  <div className={styles.compare}>
                    <StateStripCard series={series} axis={axis} />
                    <div
                      className={styles.grid}
                      data-solo={expanded !== null}
                      style={{ ['--card-min' as string]: `${cardMin}px` }}
                    >
                      {gridChannels.map((c) => (
                        <TimeSeriesCard
                          key={c.key}
                          channel={c}
                          series={series}
                          axis={axis}
                          expanded={expanded === c.key}
                          onExpand={onExpand}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <SignalStack
                    channels={visibleChannels}
                    series={series}
                    axis={axis}
                    notice={notice}
                    read={telemetryRead}
                    events={windowEvents}
                  />
                )}

                {/* THE LEDGER ROW carries the ground track beside it.

                    `auterion-flight-log-review.png` pairs a map track with a
                    plot so position and signal are read together, and this is
                    where the pairing costs nothing: the track is near-square
                    at this height, while beside the stack it would be a tall
                    column two thirds empty — and every pixel it did not take
                    is time resolution the traces got instead. Below 1440 the
                    column is dropped rather than squeezed to a thumbnail; the
                    rail still carries the fix as numbers. */}
                <div className={styles.ledgerRow} data-open={ledgerOpen}>
                  {ledgerOpen ? (
                    <ReadingsLedger
                      series={series}
                      channels={ledgerChannels}
                      axis={axis}
                      loading={primaryLoading}
                      tools={ledgerTool}
                      read={telemetryRead}
                      robotNames={robotNames}
                    />
                  ) : (
                    <Panel
                      title="Readings"
                      count={figure(telemetryRead, num(coverage.inWindow))}
                      meta={
                        <span className={styles.paneMeta}>
                          {`in window · ${duration(transport.to - transport.from)}`}
                        </span>
                      }
                      tools={ledgerTool}
                      pad="none"
                      scroll={false}
                    />
                  )}
                  {ledgerOpen && (
                    <TrackCard series={series} axis={axis} read={telemetryRead} />
                  )}
                </div>
              </>
          </div>
        </div>

        <TransportBar
          transport={transport}
          series={series}
          axis={axis}
          onAxis={setAxis}
          now={now}
          read={telemetryRead}
          events={events}
        />
      </CursorCtx.Provider>
    </div>
  );
}
